/**
 * Sticky Navigation Handler
 * Manages sticky navigation behavior for both mobile and desktop viewports
 * with dynamic navigation generation and responsive state management
 * Includes teleport functionality to move nav from footer to content areas
 */
document.addEventListener('DOMContentLoaded', () => {
    /**
     * Teleports sticky-nav from footer to appropriate content areas
     */
    const teleportStickyNav = () => {
        const stickyNavFromFooter = document.querySelector('.sticky-nav-hidden');
        if (!stickyNavFromFooter) {
            return;
        }

        // Check if already teleported to avoid duplicates
        const existingTeleported = document.querySelector('.sticky-nav:not(.sticky-nav-hidden)');
        if (existingTeleported) {
            return;
        }

        /**
         * Checks if sticky navigation should be disabled via ACF field
         */
        const isStickyNavDisabled = () => {
            const body = document.body;

            // For now, let's skip ACF field checking and just check for scroll links
            // The ACF field checking can be added later when we know the field structure
            return false;
        };

        /**
         * Checks if there are any scroll link sections available
         */
        const hasScrollLinks = () => {
            const sections = document.querySelectorAll('[data-link][data-link-title]');
            return sections.length > 0;
        };

        /**
         * Removes any existing sticky-nav blocks from content (but NOT from footer)
         */
        const removeExistingStickyNavs = () => {
            // Remove from .entry-content, .pd__scroll, and .pd__content, NOT from footer
            const entryContent = document.querySelector('.entry-content');
            const pdScroll = document.querySelector('.pd__scroll');
            const pdContent = document.querySelector('.pd__content');

            if (entryContent) {
                const existingInEntry = entryContent.querySelectorAll('.sticky-nav');
                existingInEntry.forEach(nav => nav.remove());
            }

            if (pdScroll) {
                const existingInPdScroll = pdScroll.querySelectorAll('.sticky-nav');
                existingInPdScroll.forEach(nav => nav.remove());
            }

            if (pdContent) {
                const existingInPdContent = pdContent.querySelectorAll('.sticky-nav');
                existingInPdContent.forEach(nav => nav.remove());
            }
        };

        if (isStickyNavDisabled() || !hasScrollLinks()) {
            return;
        }

        // Check for product page first
        const pdScroll = document.querySelector('.pd__scroll');
        if (pdScroll) {
            removeExistingStickyNavs();

            const clonedNav = stickyNavFromFooter.cloneNode(true);
            clonedNav.classList.remove('sticky-nav-hidden');
            clonedNav.classList.add('sticky-nav');

            pdScroll.appendChild(clonedNav);
        } else {
            // Otherwise teleport to .entry-content
            const entryContent = document.querySelector('.entry-content');
            if (!entryContent) {
                return;
            }

            const children = Array.from(entryContent.children);
            if (children.length < 2) {
                return;
            }

            removeExistingStickyNavs();

            const clonedNav = stickyNavFromFooter.cloneNode(true);
            clonedNav.classList.remove('sticky-nav-hidden');
            clonedNav.classList.add('sticky-nav');

            // Insert as third child (index 2)
            if (children.length >= 2) {
                // Insert after the second child (index 1)
                const secondChild = children[1];
                if (secondChild.nextSibling) {
                    entryContent.insertBefore(clonedNav, secondChild.nextSibling);
                } else {
                    entryContent.appendChild(clonedNav);
                }
            } else {
                entryContent.appendChild(clonedNav);
            }
        }
    };

    let stickyNav = document.querySelector('.sticky-nav:not(.sticky-nav-hidden)');
    if (!stickyNav) {
        // If no sticky nav found, try to teleport first
        teleportStickyNav();
        stickyNav = document.querySelector('.sticky-nav:not(.sticky-nav-hidden)');
        if (!stickyNav) return;
    }

    const stickyNavContainer = stickyNav.querySelector('.sticky-nav__container');
    const stickyNavList = stickyNav.querySelector('.sticky-nav__list');
    const stickyNavToggle = stickyNav.querySelector('.sticky-nav__toggle');
    const siteHeader = document.querySelector('.site-header');

    const isMobile = () => window.innerWidth < 1280;
    const HEADER_OFFSET = 260;
    const SCROLL_THRESHOLD = 50;

    let initialPosition = 0;
    let isNavOpen = false;
    let isInitialized = false;
    let scrollTimeout = null;
    let resizeTimeout = null;
    let lastSectionsCount = 0;
    let checkInterval = null;
    let currentViewport = isMobile() ? 'mobile' : 'desktop';
    let spacerElement = null;
    let wasInFixedContainer = false;
    let lastScrollY = 0;
    let lastHeaderTop = 0;

    /**
     * Adds a spacer element to prevent content jump when nav becomes sticky
     */
    const addSpacer = () => {
        if (spacerElement) return; // Already has spacer

        const navHeight = stickyNav.offsetHeight;
        spacerElement = document.createElement('div');
        spacerElement.className = 'sticky-nav-spacer';
        spacerElement.style.height = `${navHeight}px`;
        spacerElement.style.width = '100%';

        // Insert spacer after the sticky nav
        stickyNav.parentNode.insertBefore(spacerElement, stickyNav.nextSibling);
    };

    /**
     * Removes the spacer element when nav is no longer sticky
     */
    const removeSpacer = () => {
        if (spacerElement) {
            spacerElement.remove();
            spacerElement = null;
        }
    };

    /**
     * Generates navigation items from sections with data-link attributes
     * Only regenerates when section count changes to prevent unnecessary DOM manipulation
     */
    const generateNavigation = () => {
        if (!stickyNavList) return;

        const sections = document.querySelectorAll('[data-link][data-link-title]');

        if (sections.length === 0) {
            stickyNavList.innerHTML = '';
            lastSectionsCount = 0;
            return;
        }

        if (sections.length === lastSectionsCount) {
            return;
        }

        lastSectionsCount = sections.length;
        stickyNavList.innerHTML = '';

        sections.forEach((section) => {
            const link = section.getAttribute('data-link');
            const title = section.getAttribute('data-link-title');

            if (!link || !title) return;

            const li = document.createElement('li');
            li.classList.add('type-text-black');
            const button = document.createElement('button');
            button.textContent = title;
            button.setAttribute('data-target', link);
            button.classList.add('sticky-nav__link');

            button.addEventListener('click', (e) => {
                e.preventDefault();
                scrollToSection(section);
                if (isMobile()) {
                    closeNav();
                }
            });

            li.appendChild(button);
            stickyNavList.appendChild(li);
        });
    };

    /**
     * Smoothly scrolls to target section with header offset
     */
    const scrollToSection = (section) => {
        const rect = section.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = rect.top + scrollTop - HEADER_OFFSET;

        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
        });
    };

    /**
     * Toggles mobile navigation state
     */
    const toggleNav = () => {
        isNavOpen = !isNavOpen;
        stickyNavToggle.classList.toggle('active', isNavOpen);
        stickyNav.classList.toggle('nav-open', isNavOpen);
    };

    /**
     * Closes mobile navigation
     */
    const closeNav = () => {
        isNavOpen = false;
        stickyNavToggle.classList.remove('active');
        stickyNav.classList.remove('nav-open');
    };

    /**
     * Updates active navigation item based on current scroll position
     * Uses viewport visibility and closest section fallback for better UX
     */
    const updateActiveNavItem = () => {
        if (!stickyNavList) return;

        const sections = document.querySelectorAll('[data-link][data-link-title]');
        if (sections.length === 0) {
            const navLinks = stickyNavList.querySelectorAll('.sticky-nav__link');
            navLinks.forEach((link) => {
                link.classList.remove('active');
            });
            return;
        }

        const scrollPosition = window.pageYOffset + HEADER_OFFSET + 50;
        const viewportHeight = window.innerHeight;
        const viewportBottom = scrollPosition + viewportHeight;

        let currentSection = null;
        let closestSection = null;
        let closestDistance = Infinity;

        sections.forEach((section) => {
            const sectionTop = section.offsetTop;
            const sectionBottom = sectionTop + section.offsetHeight;
            const sectionCenter = sectionTop + (section.offsetHeight / 2);

            const isSectionVisible = sectionBottom > scrollPosition && sectionTop < viewportBottom;

            if (isSectionVisible) {
                if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                    currentSection = section.getAttribute('data-link');
                }

                const distance = Math.abs(scrollPosition - sectionCenter);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestSection = section.getAttribute('data-link');
                }
            }
        });

        const activeSection = currentSection || closestSection;
        const navLinks = stickyNavList.querySelectorAll('.sticky-nav__link');
        navLinks.forEach((link) => {
            const isActive = activeSection && link.getAttribute('data-target') === activeSection;
            link.classList.toggle('active', isActive);
        });

        // Debug mode removed
    };

    /**
     * Checks if sticky-nav is inside a fixed .pd__scroll container
     */
    const isInsideFixedPdScroll = () => {
        const pdScroll = stickyNav.closest('.pd__scroll');
        return pdScroll && pdScroll.classList.contains('is-fixed');
    };

    /**
     * Handles sticky navigation behavior for both mobile and desktop
     * Mobile: slides from bottom after scroll threshold
     * Desktop: Nav sticks naturally via CSS, we only manage hiding the header when nav reaches it
     * Note: If inside a fixed .pd__scroll, we still manage header but with different logic
     */
    const handleStickyBehavior = () => {
        if (!stickyNav) {
            return;
        }

        const isFixedContainer = isInsideFixedPdScroll();
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;

        const headerTop = siteHeader ? siteHeader.style.top : 'N/A';

        // If inside a fixed .pd__scroll, we're already fixed, so skip mobile sticky behavior
        // but still manage header on desktop
        if (isFixedContainer) {
            // Remove mobile sticky classes and spacer
            stickyNav.classList.remove('is-sticky-nav');
            removeSpacer();

            // Check if we just transitioned to fixed container
            const justBecameFixed = !wasInFixedContainer;

            // On desktop, keep header hidden while in fixed container mode
            if (!isMobile() && siteHeader) {
                if (justBecameFixed) {
                    // Capture current header position when entering fixed mode
                    const currentTop = parseFloat(siteHeader.style.top) || -54;
                    lastHeaderTop = currentTop;
                    lastScrollY = scrollY;

                    // Just update CSS variables without changing header position
                    if (window.appHeight) {
                        window.appHeight();
                    }
                } else {
                    // Only hide MORE if scrolling down, never reveal while fixed
                    const scrollDelta = scrollY - lastScrollY;

                    if (scrollDelta > 0) {
                        // Scrolling DOWN - continue hiding
                        const newHeaderTop = lastHeaderTop - scrollDelta;

                        // Clamp to reasonable bounds (-226px to -54px)
                        const clampedTop = Math.max(-226, Math.min(-54, newHeaderTop));
                        siteHeader.style.top = `${clampedTop}px`;

                        lastHeaderTop = clampedTop;
                    }
                    // If scrolling UP, keep header at current position (don't reveal)

                    lastScrollY = scrollY;

                    if (window.appHeight) {
                        window.appHeight();
                    }
                }
            } else if (isMobile() && siteHeader) {
                // Reset header on mobile when in fixed container
                siteHeader.style.top = '';
                // Update CSS variables after resetting header
                if (window.appHeight) {
                    window.appHeight();
                }
            }

            wasInFixedContainer = true;

            // Still update active nav items
            updateActiveNavItem();
            return;
        }

        // Not in fixed container anymore - reset tracking
        if (wasInFixedContainer) {
            lastScrollY = 0;
            lastHeaderTop = 0;
        }
        wasInFixedContainer = false;

        // Normal behavior when NOT inside fixed container

        if (initialPosition === 0) {
            initialPosition = stickyNav.offsetTop;
            stickyNav.setAttribute('data-initial-position', initialPosition);
        }

        if (isMobile()) {
            // Mobile behavior: slide up from bottom with fixed positioning
            if (scrollY > (initialPosition + SCROLL_THRESHOLD)) {
                stickyNav.classList.add('is-sticky-nav');
                addSpacer(); // Need spacer for mobile fixed positioning
            } else {
                stickyNav.classList.remove('is-sticky-nav');
                removeSpacer();
            }
            // Reset header on mobile
            if (siteHeader) {
                siteHeader.style.top = '';
                // Update CSS variables after resetting header
                if (window.appHeight) {
                    window.appHeight();
                }
            }
        } else {
            // Desktop behavior: Nav uses CSS sticky (no spacer needed), we only adjust header
            removeSpacer(); // Clean up any spacer from mobile

            if (!siteHeader) {
                // No header management needed
                return;
            }

            // Header facts (desktop 1280px+):
            // - Header base: 154px
            // - Level-2 (when open): +72px
            // - Default position: top: -54px (54px hidden above viewport)
            // - Visible height: 154px - 54px = 100px (no menu) or 226px - 54px = 172px (with menu)

            const headerBaseHeight = 154;
            const headerDefaultTop = -54;

            // Check if level-2 menu is open (desktop only, 1280px+)
            const isDesktop = window.innerWidth >= 1280;
            const hasOpenLevel2 = isDesktop && document.querySelector('.menu-item.open');
            const level2Height = hasOpenLevel2 ? 72 : 0;

            // Calculate header dimensions
            const headerFullHeight = headerBaseHeight + level2Height; // 154px or 226px
            const headerVisibleHeight = headerFullHeight + headerDefaultTop; // 100px or 172px

            // Get nav's distance from top of viewport
            const navRect = stickyNav.getBoundingClientRect();
            const navDistanceFromTop = navRect.top;

            if (navDistanceFromTop > headerVisibleHeight) {
                // Nav hasn't reached header bottom yet - keep header at default
                siteHeader.style.top = '';

            } else if (navDistanceFromTop > 0) {
                // Nav is within header range - push header up progressively
                // Example: nav at 100px, visible height 172px → overlap 72px → top: -54 - 72 = -126px
                const overlap = headerVisibleHeight - navDistanceFromTop;
                const headerTopValue = headerDefaultTop - overlap;

                siteHeader.style.top = `${headerTopValue}px`;

            } else {
                // Nav has reached top (0px) - header fully hidden
                // 154px - 54px = -154px (no menu) or 226px - 54px = -226px (with menu)
                const fullyHidden = headerDefaultTop - headerVisibleHeight;
                siteHeader.style.top = `${fullyHidden}px`;
            }

            // Update CSS variables after changing header position
            if (window.appHeight) {
                window.appHeight();
            }
        }

        updateActiveNavItem();
    };

    /**
     * Handles header positioning when sticky-nav is inside a fixed .pd__scroll container
     * The nav is already fixed at top, so we manage header based on scroll position
     * Only runs on desktop (mobile resets header)
     */
    const handleHeaderForFixedContainer = () => {
        if (!siteHeader || isMobile()) {
            return;
        }

        // Header facts (desktop 1280px+):
        // - Header base: 154px
        // - Level-2 (when open): +72px
        // - Default position: top: -54px
        // - Visible height: 100px (no menu) or 172px (with menu)

        const headerBaseHeight = 154;
        const headerDefaultTop = -54;

        // Check if level-2 menu is open (desktop only)
        const isDesktop = window.innerWidth >= 1280;
        const hasOpenLevel2 = isDesktop && document.querySelector('.menu-item.open');
        const level2Height = hasOpenLevel2 ? 72 : 0;

        const headerFullHeight = headerBaseHeight + level2Height;
        const headerVisibleHeight = headerFullHeight + headerDefaultTop;

        // When .pd__scroll is fixed, the sticky-nav is at the top (0px from viewport top)
        // We want to hide the header when scrolling down
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;

        // Get the point where .pd__scroll became fixed (bottom of .pd__hero)
        const pdHero = document.querySelector('.pd__hero');
        if (!pdHero) {
            return;
        }

        const pdHeroRect = pdHero.getBoundingClientRect();
        const pdHeroBottom = pdHeroRect.bottom + scrollY;
        const scrollSinceFixed = scrollY - pdHeroBottom;

        // Hide header progressively as we scroll past the fixed point
        if (scrollSinceFixed <= 0) {
            // Not yet past the fixed point
            siteHeader.style.top = '';
        } else if (scrollSinceFixed < headerVisibleHeight) {
            // Scrolling past, gradually hide header
            const overlap = scrollSinceFixed;
            const headerTopValue = headerDefaultTop - overlap;
            siteHeader.style.top = `${headerTopValue}px`;
        } else {
            // Fully scrolled past, hide header completely
            const fullyHidden = headerDefaultTop - headerVisibleHeight;
            siteHeader.style.top = `${fullyHidden}px`;
        }

        // Update CSS variables after changing header position
        if (window.appHeight) {
            window.appHeight();
        }
    };

    /**
     * Reset header to default position
     */
    const resetHeader = () => {
        if (siteHeader) {
            siteHeader.style.top = '';
        }
    };

    /**
     * Handles viewport changes and reinitializes navigation if needed
     * Ensures proper behavior when switching between mobile/desktop
     */
    const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            const newViewport = isMobile() ? 'mobile' : 'desktop';

            if (currentViewport !== newViewport) {
                currentViewport = newViewport;

                if (!isMobile()) {
                    closeNav();
                }

                // Reset header position when switching viewports
                resetHeader();

                generateNavigation();
                handleStickyBehavior();
            } else {
                handleStickyBehavior();
            }
        }, 100);
    };

    /**
     * Checks for new sections periodically to handle dynamic content
     */
    const checkForNewSections = () => {
        const currentSections = document.querySelectorAll('[data-link][data-link-title]');
        if (currentSections.length !== lastSectionsCount) {
            generateNavigation();
        }
    };

    /**
     * Cleans up all timeouts and intervals
     */
    const cleanup = () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        if (resizeTimeout) clearTimeout(resizeTimeout);
        if (checkInterval) clearInterval(checkInterval);
        resetHeader();
    };

    // Event listeners
    if (stickyNavToggle) {
        stickyNavToggle.addEventListener('click', toggleNav);
    }

    window.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(handleStickyBehavior, 10);
    });

    window.addEventListener('resize', handleResize);

    // Initialize
    if (!isInitialized) {
        generateNavigation();
        handleStickyBehavior();
        isInitialized = true;
    }

    // Setup periodic section checking
    setTimeout(() => {
        checkInterval = setInterval(checkForNewSections, 2000);
    }, 1000);

    // Handle scroll to form button for product pages
    const scrollToFormButton = stickyNav.querySelector('.scroll-to-form');
    if (scrollToFormButton) {
        scrollToFormButton.addEventListener('click', (e) => {
            e.preventDefault();
            const form = document.querySelector('form[name="add-to-cart"]');
            const featureForm = document.querySelector('.pd__details--actions--feature');
            const targetForm = form || featureForm;

            if (targetForm) {
                scrollToSection(targetForm);
            }
        });
    }

    // Re-teleport on window resize (in case content changes)
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            teleportStickyNav();
        }, 100);
    });

    // Re-teleport when new content is loaded (for dynamic content)
    const observer = new MutationObserver((mutations) => {
        let shouldRecheck = false;

        mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
                const addedNodes = Array.from(mutation.addedNodes);
                const hasRelevantContent = addedNodes.some(node =>
                    node.nodeType === Node.ELEMENT_NODE &&
                    (node.classList?.contains('entry-content') ||
                     node.querySelector?.('.entry-content') ||
                     node.classList?.contains('pd__scroll') ||
                     node.querySelector?.('.pd__scroll'))
                );

                if (hasRelevantContent) {
                    shouldRecheck = true;
                }
            }
        });

        if (shouldRecheck) {
            setTimeout(teleportStickyNav, 100);
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    // Cleanup on page unload
    window.addEventListener('beforeunload', cleanup);
});
